import { useState, useMemo, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Home as HomeIcon, Database, GraduationCap, Search, Bell, Settings as SettingsIcon,
  HelpCircle, Menu as MenuIcon, Star, ChevronDown, RotateCcw, ArrowLeft, TerminalSquare,
  Sparkles, Plus, Sun, Moon, MonitorSmartphone, X, CheckCircle2, XCircle, Clock, Layers, ShieldCheck, Scale, KeyRound, Users,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAzure } from "@/context/AzureContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import AzureIcon from "@/components/azure/AzureIcon";
import EntraUsers from "@/components/azure/EntraUsers";
import EntraGroups from "@/components/azure/EntraGroups";
import ResourceGroups from "@/components/azure/ResourceGroups";
import RbacView from "@/components/azure/RbacView";
import AzLabs from "@/components/azure/AzLabs";
import CloudShell from "@/components/azure/CloudShell";
import AzureAssistant from "@/components/azure/AzureAssistant";
import AllResources from "@/components/azure/AllResources";
import { VirtualNetworks, Nsgs, PublicIps } from "@/components/azure/Networking";
import { VirtualMachines, Disks } from "@/components/azure/Compute";
import ScaleSets from "@/components/azure/ScaleSets";
import ResourceGraph from "@/components/azure/ResourceGraph";
import Licensing from "@/components/azure/Licensing";
import Mfa from "@/components/azure/Mfa";
import ConditionalAccess from "@/components/azure/ConditionalAccess";
import Pim from "@/components/azure/Pim";
import IdentityProtection from "@/components/azure/IdentityProtection";
import AzureSql from "@/components/azure/AzureSql";
import StorageAccounts from "@/components/azure/StorageAccounts";
import AzurePolicy from "@/components/azure/AzurePolicy";
import AppServices from "@/components/azure/AppServices";
import MonitorAlerts from "@/components/azure/MonitorAlerts";
import LogAnalytics from "@/components/azure/LogAnalytics";
import KeyVault from "@/components/azure/KeyVault";
import LoadBalancers from "@/components/azure/LoadBalancers";
import Operations from "@/components/azure/Operations";
import NetworkWatcher from "@/components/azure/NetworkWatcher";
import GuidedPanel from "@/components/azure/GuidedPanel";
import "./azurePortal.css";

const NAV = [
  { section: "General", items: [
    { id: "home", label: "Home", lucide: HomeIcon },
    { id: "dashboard", label: "Dashboard", img: "dashboard" },
    { id: "all-resources", label: "All resources", img: "all-resources" },
    { id: "subscriptions", label: "Subscriptions", img: "subscription" },
    { id: "resource-groups", label: "Resource groups", img: "resource-group" },
    { id: "policy", label: "Policy", lucide: ShieldCheck },
    { id: "resource-graph", label: "Resource Graph", lucide: Database },
    { id: "activity", label: "Activity log", img: "activity-log" },
  ]},
  { section: "Compute", items: [
    { id: "vms", label: "Virtual machines", img: "virtual-machine" },
    { id: "vmss", label: "Scale sets", lucide: Layers },
    { id: "disks", label: "Disks & snapshots", img: "disk" },
  ]},
  { section: "Networking", items: [
    { id: "vnets", label: "Virtual networks", img: "virtual-network" },
    { id: "nsgs", label: "Network security groups", img: "nsg" },
    { id: "public-ips", label: "Public IP addresses", img: "public-ip" },
    { id: "load-balancers", label: "Load balancers", lucide: Scale },
    { id: "network-watcher", label: "Network Watcher", img: "network-watcher" },
  ]},
  { section: "Security", items: [
    { id: "key-vaults", label: "Key vaults", lucide: KeyRound },
  ]},
  { section: "Databases", items: [
    { id: "sql", label: "Azure SQL", img: "sql" },
  ]},
  { section: "Web & Storage", items: [
    { id: "app-services", label: "App Services", img: "app-service" },
    { id: "storage", label: "Storage accounts", img: "storage-account" },
  ]},
  { section: "Monitoring & Operations", items: [
    { id: "monitor", label: "Monitor & alerts", img: "monitor" },
    { id: "log-analytics", label: "Log Analytics (KQL)", lucide: Database },
    { id: "action-groups", label: "Action groups", lucide: Users },
    { id: "activity-alerts", label: "Activity log alerts", lucide: Bell },
    { id: "operations", label: "IT Operations", img: "activity-log" },
  ]},
  { section: "Microsoft Entra ID", items: [
    { id: "entra-users", label: "Users", img: "users" },
    { id: "entra-groups", label: "Groups", img: "groups" },
    { id: "rbac", label: "Roles & RBAC", img: "roles" },
    { id: "licensing", label: "Licenses", img: "licenses" },
    { id: "mfa", label: "Per-user MFA", img: "mfa" },
    { id: "conditional-access", label: "Conditional Access", img: "conditional-access" },
    { id: "pim", label: "Privileged Identity (PIM)", img: "pim" },
    { id: "identity-protection", label: "Identity Protection", img: "id-protection" },
  ]},
  { section: "Training", items: [
    { id: "labs", label: "Labs", lucide: GraduationCap },
  ]},
];

const ALL_ITEMS = NAV.flatMap((g) => g.items);
const ITEM_BY_ID = Object.fromEntries(ALL_ITEMS.map((i) => [i.id, i]));

const VIEW_TITLES = {
  home: "Home", dashboard: "Dashboard", "all-resources": "All resources", subscriptions: "Subscriptions",
  "resource-groups": "Resource groups", "resource-graph": "Resource Graph Explorer",
  "policy": "Policy | Governance",
  "vmss": "Virtual machine scale sets",
  activity: "Activity log", vms: "Virtual machines", disks: "Disks & snapshots",
  vnets: "Virtual networks", nsgs: "Network security groups", "public-ips": "Public IP addresses",
  "entra-users": "Users", "entra-groups": "Groups", rbac: "Roles & RBAC",
  licensing: "Licenses", mfa: "Per-user MFA", "conditional-access": "Conditional Access",
  pim: "Privileged Identity Management", "identity-protection": "Identity Protection",
  "log-analytics": "Log Analytics (KQL)", "action-groups": "Action groups", "activity-alerts": "Activity log alerts",
  monitor: "Monitor & alerts",
  labs: "Training Labs",
};

function NavGlyph({ item, size = 18 }) {
  if (!item) return <AzureIcon icon="generic" size={size} />;
  if (item.img) return <AzureIcon icon={item.img} size={size} />;
  const L = item.lucide;
  return <L style={{ width: size, height: size, color: "currentColor" }} />;
}

function HeaderBtn({ children, title, onClick, testid }) {
  return <button data-testid={testid} title={title} onClick={onClick} className="h-9 w-9 grid place-items-center rounded hover:bg-white/10 text-white/85 hover:text-white">{children}</button>;
}

export default function AzurePortal() {
  const { state, loading, persona } = useAzure();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [view, setViewRaw] = useState("home");
  const [shell, setShell] = useState(false);
  const [assistant, setAssistant] = useState(false);
  const [q, setQ] = useState("");
  const [showResults, setShowResults] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [guidedOpen, setGuidedOpen] = useState(false);
  const [guidedDocked, setGuidedDocked] = useState(false);
  const [guidedParam, setGuidedParam] = useState(null);
  useEffect(() => {
    const g = new URLSearchParams(window.location.search).get("guided");
    if (g) { setGuidedParam(g === "1" ? null : g); setGuidedOpen(true); }
  }, []);

  const [theme, setTheme] = useState(() => localStorage.getItem("az_theme") || "light");
  const [menuMode, setMenuMode] = useState(() => localStorage.getItem("az_menu_mode") || "docked");
  const [flyoutOpen, setFlyoutOpen] = useState(false);
  const [sysDark, setSysDark] = useState(() => window.matchMedia?.("(prefers-color-scheme: dark)").matches);
  useEffect(() => {
    const mq = window.matchMedia?.("(prefers-color-scheme: dark)");
    if (!mq) return;
    const fn = (e) => setSysDark(e.matches);
    mq.addEventListener?.("change", fn);
    return () => mq.removeEventListener?.("change", fn);
  }, []);
  const isDark = theme === "dark" || (theme === "auto" && sysDark);
  useEffect(() => { localStorage.setItem("az_theme", theme); }, [theme]);
  useEffect(() => { localStorage.setItem("az_menu_mode", menuMode); }, [menuMode]);

  const [favorites, setFavorites] = useState(() => { try { return JSON.parse(localStorage.getItem("az_favorites")) || []; } catch { return []; } });
  const [recent, setRecent] = useState(() => { try { return JSON.parse(localStorage.getItem("az_recent")) || []; } catch { return []; } });
  const toggleFav = useCallback((id) => setFavorites((f) => { const n = f.includes(id) ? f.filter((x) => x !== id) : [...f, id]; localStorage.setItem("az_favorites", JSON.stringify(n)); return n; }), []);
  const setView = useCallback((id) => {
    setViewRaw(id);
    setFlyoutOpen(false);
    if (id !== "home" && id !== "dashboard" && VIEW_TITLES[id]) {
      setRecent((r) => { const n = [id, ...r.filter((x) => x !== id)].slice(0, 6); localStorage.setItem("az_recent", JSON.stringify(n)); return n; });
    }
  }, []);

  const [seen, setSeen] = useState(() => localStorage.getItem("az_notif_seen") || "");
  const notifItems = state?.activity_log || [];
  const unread = notifItems.filter((n) => !seen || n.timestamp > seen).length;
  const openNotif = () => {
    setNotifOpen((o) => {
      const nv = !o;
      if (nv && notifItems[0]) { localStorage.setItem("az_notif_seen", notifItems[0].timestamp); setSeen(notifItems[0].timestamp); }
      return nv;
    });
  };

  const searchResults = useMemo(() => {
    if (!q.trim() || !state) return null;
    const s = q.toLowerCase();
    const r = { Services: [], Users: [], Groups: [], "Resource groups": [], Subscriptions: [], Resources: [] };
    ALL_ITEMS.forEach((it) => { if (it.label.toLowerCase().includes(s)) r.Services.push({ label: it.label, sub: "Service", view: it.id, item: it }); });
    state.entra_users.forEach((u) => { if ((u.display_name + u.upn).toLowerCase().includes(s)) r.Users.push({ label: u.display_name, sub: u.upn, view: "entra-users", img: "users" }); });
    state.entra_groups.forEach((g) => { if (g.name.toLowerCase().includes(s)) r.Groups.push({ label: g.name, sub: g.group_type, view: "entra-groups", img: "groups" }); });
    state.resource_groups.forEach((g) => { if (g.name.toLowerCase().includes(s)) r["Resource groups"].push({ label: g.name, sub: g.location, view: "resource-groups", img: "resource-group" }); });
    state.subscriptions.forEach((g) => { if (g.name.toLowerCase().includes(s)) r.Subscriptions.push({ label: g.name, sub: g.state, view: "subscriptions", img: "subscription" }); });
    const viewForType = (t) => t.includes("virtualMachines") ? "vms" : t.includes("virtualNetworks") ? "vnets" : t.includes("networkSecurityGroups") ? "nsgs" : t.includes("publicIP") ? "public-ips" : t.includes("disks") || t.includes("snapshots") ? "disks" : "all-resources";
    (state.resources || []).forEach((res) => { if (res.name.toLowerCase().includes(s)) r.Resources.push({ label: res.name, sub: res.type.split("/").slice(-1)[0], view: viewForType(res.type), type: res.type }); });
    return r;
  }, [q, state]);

  const doReset = () => {
    if (!window.confirm("Reset your entire Azure sandbox to the seeded starting state? This clears all users, groups, resources and lab progress you created.")) return;
    api.post("/azure/reset").then(() => window.location.reload());
  };
  const switchPersona = (pid) => api.post("/azure/persona", { persona_id: pid }).then(() => window.location.reload());

  if (loading || !state) {
    return <div className="az-portal h-screen grid place-items-center text-muted-foreground" style={{ background: "var(--az-pane)" }}>Loading Azure Lab…</div>;
  }

  const favItems = favorites.map((id) => ITEM_BY_ID[id]).filter(Boolean);

  const PortalMenu = () => (
    <nav className="py-2 h-full overflow-y-auto" style={{ background: "var(--az-nav-bg)", color: "var(--az-nav-fg)" }} data-testid="az-portal-menu">
      <button className="az-nav-item" data-testid="az-create-resource" onClick={() => setView("all-resources")}>
        <Plus className="h-[18px] w-[18px] text-[#50c8ff]" /> <span className="font-medium">Create a resource</span>
      </button>
      <div className="h-px my-1.5" style={{ background: "var(--az-nav-border)" }} />
      {favItems.length > 0 && (
        <div className="mb-1.5">
          <div className="px-4 py-1 text-[11px] uppercase tracking-wider az-nav-muted flex items-center gap-1"><Star className="h-3 w-3 fill-current" /> Favorites</div>
          {favItems.map((it) => (
            <button key={it.id} className={`az-nav-item ${view === it.id ? "active" : ""}`} onClick={() => setView(it.id)}>
              <NavGlyph item={it} /> <span className="flex-1 truncate">{it.label}</span>
            </button>
          ))}
          <div className="h-px my-1.5" style={{ background: "var(--az-nav-border)" }} />
        </div>
      )}
      {NAV.map((grp) => (
        <div key={grp.section} className="mb-1">
          <div className="px-4 py-1 text-[11px] uppercase tracking-wider az-nav-muted">{grp.section}</div>
          {grp.items.map((it) => (
            <div key={it.id} className="group relative flex items-center">
              <button data-testid={`az-nav-${it.id}`} onClick={() => setView(it.id)} className={`az-nav-item flex-1 ${view === it.id ? "active" : ""}`}>
                <NavGlyph item={it} /> <span className="flex-1 truncate">{it.label}</span>
              </button>
              <button data-testid={`az-fav-${it.id}`} onClick={() => toggleFav(it.id)} title={favorites.includes(it.id) ? "Unfavorite" : "Favorite"}
                className="absolute right-2 opacity-0 group-hover:opacity-100 focus:opacity-100 az-nav-muted hover:text-[#ffd23f]">
                <Star className={`h-3.5 w-3.5 ${favorites.includes(it.id) ? "fill-[#ffd23f] text-[#ffd23f]" : ""}`} />
              </button>
            </div>
          ))}
        </div>
      ))}
    </nav>
  );

  return (
    <div className={`az-portal ${isDark ? "az-dark" : ""} min-h-screen flex flex-col`} style={{ background: "var(--az-pane)" }}>
      <header className="h-12 flex items-center px-2 gap-1 shrink-0 sticky top-0 z-30" style={{ background: "var(--az-header-bg)", color: "var(--az-header-fg)", borderBottom: "1px solid var(--az-header-border)" }}>
        <button className="h-9 w-9 grid place-items-center rounded hover:bg-white/10" data-testid="az-menu-toggle" title="Portal menu"
          onClick={() => menuMode === "flyout" ? setFlyoutOpen((o) => !o) : setMenuMode("flyout")}>
          <MenuIcon className="h-5 w-5" />
        </button>
        <button className="flex items-center gap-2 hover:bg-white/10 rounded px-2 h-9" onClick={() => setView("home")} data-testid="az-logo">
          <AzureIcon icon="entra" size={20} />
          <span className="font-semibold text-sm hidden sm:inline">HMG Azure</span>
        </button>
        <span className="hidden md:inline text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[#8a6d00] text-[#ffe89e] tracking-wide" data-testid="az-training-badge">TRAINING ENVIRONMENT</span>

        <div className="relative flex-1 max-w-2xl mx-auto">
          <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-white/50" />
          <Input data-testid="az-global-search" value={q}
            onChange={(e) => { setQ(e.target.value); setShowResults(true); }}
            onFocus={() => setShowResults(true)}
            onBlur={() => setTimeout(() => setShowResults(false), 150)}
            placeholder="Search resources, services and docs"
            className="h-8 pl-8 bg-white/10 border-white/20 text-white placeholder:text-white/50 focus-visible:ring-[#50c8ff]" />
          {showResults && searchResults && (
            <div className="absolute top-10 left-0 right-0 bg-popover text-popover-foreground border border-border rounded-md shadow-2xl z-40 max-h-96 overflow-y-auto" data-testid="az-search-results">
              {Object.entries(searchResults).every(([, v]) => v.length === 0) ? (
                <div className="px-4 py-3 text-sm text-muted-foreground">No results</div>
              ) : Object.entries(searchResults).map(([cat, items]) => items.length > 0 && (
                <div key={cat}>
                  <div className="px-3 pt-2 pb-1 text-[11px] uppercase tracking-widest text-muted-foreground">{cat}</div>
                  {items.slice(0, 6).map((it, i) => (
                    <button key={i} className="w-full text-left px-3 py-2 hover:bg-accent flex items-center gap-2" onMouseDown={() => { setView(it.view); setQ(""); setShowResults(false); }}>
                      {it.type ? <AzureIcon type={it.type} size={18} /> : it.img ? <AzureIcon icon={it.img} size={18} /> : it.item ? <NavGlyph item={it.item} /> : <Layers className="h-4 w-4" />}
                      <span className="text-sm font-medium flex-1 truncate">{it.label}</span>
                      <span className="text-xs text-muted-foreground">{it.sub}</span>
                    </button>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>

        <HeaderBtn testid="az-cloudshell-toggle" title="Cloud Shell" onClick={() => setShell((s) => !s)}><TerminalSquare className="h-[18px] w-[18px]" /></HeaderBtn>
        <HeaderBtn testid="az-assistant-toggle" title="Copilot / Assistant" onClick={() => setAssistant((s) => !s)}><Sparkles className="h-[18px] w-[18px]" /></HeaderBtn>
        <div className="relative">
          <HeaderBtn testid="az-notifications-toggle" title="Notifications" onClick={openNotif}><Bell className="h-[18px] w-[18px]" /></HeaderBtn>
          {unread > 0 && <span className="absolute top-0.5 right-0.5 min-w-4 h-4 px-1 rounded-full bg-[#e81123] text-white text-[10px] grid place-items-center">{unread}</span>}
        </div>

        <Popover>
          <PopoverTrigger asChild><button className="h-9 w-9 grid place-items-center rounded hover:bg-white/10" data-testid="az-settings-toggle" title="Settings"><SettingsIcon className="h-[18px] w-[18px]" /></button></PopoverTrigger>
          <PopoverContent align="end" className="w-72" data-testid="az-settings-panel">
            <div className="text-sm font-semibold mb-2">Portal settings</div>
            <div className="text-xs text-muted-foreground mb-1">Theme</div>
            <div className="grid grid-cols-3 gap-1.5 mb-3">
              {[["light", "Light", Sun], ["dark", "Dark", Moon], ["auto", "Auto", MonitorSmartphone]].map(([v, l, Ic]) => (
                <button key={v} data-testid={`az-theme-${v}`} onClick={() => setTheme(v)} className={`flex flex-col items-center gap-1 py-2 rounded border text-xs ${theme === v ? "border-[#0078d4] bg-accent text-accent-foreground" : "border-border hover:bg-muted"}`}>
                  <Ic className="h-4 w-4" /> {l}
                </button>
              ))}
            </div>
            <div className="text-xs text-muted-foreground mb-1">Portal menu behavior</div>
            <div className="grid grid-cols-2 gap-1.5">
              {[["docked", "Docked"], ["flyout", "Flyout"]].map(([v, l]) => (
                <button key={v} data-testid={`az-menu-mode-${v}`} onClick={() => setMenuMode(v)} className={`py-2 rounded border text-xs ${menuMode === v ? "border-[#0078d4] bg-accent text-accent-foreground" : "border-border hover:bg-muted"}`}>{l}</button>
              ))}
            </div>
          </PopoverContent>
        </Popover>

        <HeaderBtn title="Help + support"><HelpCircle className="h-[18px] w-[18px]" /></HeaderBtn>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-2 hover:bg-white/10 rounded px-2 h-9" data-testid="az-persona-switcher">
              <div className="h-7 w-7 rounded-full bg-[#50c8ff] text-[#0e2a47] grid place-items-center text-xs font-bold">{persona?.display_name?.[0]}</div>
              <div className="hidden lg:block text-left leading-tight">
                <div className="text-xs font-medium">{persona?.display_name}</div>
                <div className="text-[10px] text-white/60">{state.tenant.name}</div>
              </div>
              <ChevronDown className="h-3.5 w-3.5 text-white/60" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-72">
            <DropdownMenuLabel>Switch identity (persona)</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {state.personas.map((p) => (
              <DropdownMenuItem key={p.id} data-testid={`az-persona-${p.display_name.replace(/\s/g, "-").toLowerCase()}`} onClick={() => switchPersona(p.id)} className="flex flex-col items-start gap-0.5">
                <div className="flex items-center gap-2 w-full"><span className="font-medium">{p.display_name}</span>{p.is_current && <span className="ml-auto text-[10px] text-[#0078d4]">Current</span>}</div>
                <span className="text-xs text-muted-foreground">{p.entra_roles.join(", ")}</span>
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={doReset} data-testid="az-reset-btn" className="text-amber-600"><RotateCcw className="h-4 w-4 mr-2" /> Reset sandbox</DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/labs")}><ArrowLeft className="h-4 w-4 mr-2" /> Back to HMG Labs</DropdownMenuItem>
            <DropdownMenuItem onClick={() => { logout(); navigate("/login"); }}>Sign out</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      <div className="flex flex-1 min-h-0">
        {menuMode === "docked" && (
          <aside className="w-64 shrink-0 hidden md:block" style={{ background: "var(--az-nav-bg)" }}><PortalMenu /></aside>
        )}
        {menuMode === "flyout" && flyoutOpen && (
          <>
            <div className="fixed inset-0 bg-black/40 z-30" onClick={() => setFlyoutOpen(false)} />
            <aside className="fixed left-0 top-12 bottom-0 w-64 z-40 shadow-2xl" style={{ background: "var(--az-nav-bg)" }}><PortalMenu /></aside>
          </>
        )}

        <main className="flex-1 min-w-0 overflow-y-auto" style={{ background: "var(--az-pane)" }}>
          <div className={`px-6 py-4 max-w-[1600px] transition-[padding] duration-200 ${guidedDocked ? "sm:pr-[396px]" : ""}`}>
            <nav className="text-xs text-muted-foreground mb-2 flex items-center gap-1" data-testid="az-breadcrumb">
              <button className="hover:text-foreground hover:underline" onClick={() => setView("home")}>Home</button>
              {view !== "home" && <><span>›</span><span className="text-foreground">{VIEW_TITLES[view]}</span></>}
            </nav>
            {view !== "home" && view !== "dashboard" && (
              <div className="flex items-center gap-2.5 mb-4">
                <NavGlyph item={ITEM_BY_ID[view] || { img: "all-resources" }} size={28} />
                <h1 className="font-semibold text-[22px] leading-none">{VIEW_TITLES[view]}</h1>
              </div>
            )}

            {view === "home" && <Home state={state} onGo={setView} recent={recent} />}
            {view === "dashboard" && <Dashboard state={state} onGo={setView} />}
            {view === "all-resources" && <AllResources />}
            {view === "subscriptions" && <Subscriptions state={state} />}
            {view === "resource-groups" && <ResourceGroups />}
            {view === "policy" && <AzurePolicy />}
            {view === "resource-graph" && <ResourceGraph />}
            {view === "activity" && <ActivityLog state={state} />}
            {view === "vms" && <VirtualMachines />}
            {view === "vmss" && <ScaleSets />}
            {view === "disks" && <Disks />}
            {view === "vnets" && <VirtualNetworks />}
            {view === "nsgs" && <Nsgs />}
            {view === "public-ips" && <PublicIps />}
            {view === "network-watcher" && <NetworkWatcher />}
            {view === "sql" && <AzureSql />}
            {view === "app-services" && <AppServices />}
            {view === "storage" && <StorageAccounts />}
            {view === "monitor" && <MonitorAlerts />}
            {view === "log-analytics" && <LogAnalytics />}
            {view === "action-groups" && <LogAnalytics initialTab="action-groups" />}
            {view === "activity-alerts" && <LogAnalytics initialTab="alerts" />}
            {view === "key-vaults" && <KeyVault />}
            {view === "load-balancers" && <LoadBalancers />}
            {view === "operations" && <Operations />}
            {view === "entra-users" && <EntraUsers />}
            {view === "entra-groups" && <EntraGroups />}
            {view === "rbac" && <RbacView />}
            {view === "licensing" && <Licensing />}
            {view === "mfa" && <Mfa />}
            {view === "conditional-access" && <ConditionalAccess />}
            {view === "pim" && <Pim />}
            {view === "identity-protection" && <IdentityProtection />}
            {view === "labs" && <AzLabs onGo={setView} />}
          </div>
        </main>

        {notifOpen && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setNotifOpen(false)} />
            <div className="fixed right-0 top-12 bottom-0 w-full max-w-sm bg-card border-l border-border shadow-2xl z-50 flex flex-col" data-testid="az-notification-center">
              <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                <div className="font-semibold text-sm">Notifications</div>
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setNotifOpen(false)}><X className="h-4 w-4" /></Button>
              </div>
              <div className="flex-1 overflow-y-auto divide-y divide-border">
                {notifItems.length === 0 && <div className="p-4 text-sm text-muted-foreground">No notifications.</div>}
                {notifItems.map((n) => (
                  <div key={n.id} className="px-4 py-3 flex items-start gap-2.5" data-testid="az-notification-item">
                    {n.status === "Succeeded" ? <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5" /> : n.status === "Failed" ? <XCircle className="h-4 w-4 text-red-500 mt-0.5" /> : <Clock className="h-4 w-4 text-amber-500 mt-0.5" />}
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium">{n.operation}</div>
                      <div className="text-xs text-muted-foreground truncate">{n.resource_name} · {n.initiated_by}</div>
                      <div className="text-[11px] text-muted-foreground">{new Date(n.timestamp).toLocaleString()}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      {shell && <CloudShell onClose={() => setShell(false)} />}
      {assistant && <AzureAssistant context={VIEW_TITLES[view]} onClose={() => setAssistant(false)} />}
      <GuidedPanel open={guidedOpen} initialScenario={guidedParam} onClose={() => setGuidedOpen(false)} setView={setView} onDockedChange={setGuidedDocked} />
      {!guidedOpen && (
        <button onClick={() => setGuidedOpen(true)} data-testid="az-guided-launch"
          className="fixed right-5 bottom-5 z-30 flex items-center gap-2 rounded-full pl-3 pr-4 py-2.5 text-white shadow-xl"
          style={{ background: "linear-gradient(135deg,#0078d4,#50e6ff)" }}>
          <GraduationCap className="h-5 w-5" /> <span className="text-sm font-medium">Guided Labs</span>
        </button>
      )}
    </div>
  );
}

function Home({ state, onGo, recent }) {
  const completed = (state.labs || []).filter((l) => l.progress?.status === "completed").length;
  const services = [
    { id: "vms", label: "Virtual machines", img: "virtual-machine" },
    { id: "vnets", label: "Virtual networks", img: "virtual-network" },
    { id: "all-resources", label: "All resources", img: "all-resources" },
    { id: "entra-users", label: "Microsoft Entra ID", img: "entra-id" },
    { id: "resource-groups", label: "Resource groups", img: "resource-group" },
    { id: "rbac", label: "Roles & RBAC", img: "roles" },
    { id: "conditional-access", label: "Conditional Access", img: "conditional-access" },
    { id: "labs", label: "Training Labs", lucide: GraduationCap },
  ];
  const navItems = [
    { id: "subscriptions", label: "Subscriptions", img: "subscription" },
    { id: "resource-groups", label: "Resource groups", img: "resource-group" },
    { id: "all-resources", label: "All resources", img: "all-resources" },
    { id: "dashboard", label: "Dashboard", img: "dashboard" },
  ];
  const resById = (id) => ITEM_BY_ID[id];
  return (
    <div data-testid="az-home">
      <div className="rounded-md text-white p-6 mb-5" style={{ background: "linear-gradient(90deg,#0e2a47,#0078d4)" }}>
        <div className="text-xs uppercase tracking-widest text-white/70">Directory</div>
        <div className="font-semibold text-2xl mt-1">{state.tenant.name}</div>
        <div className="text-white/70 text-sm">{state.tenant.primary_domain} · Tenant {state.tenant.id.slice(0, 8)}…</div>
      </div>

      <div className="text-sm font-semibold mb-2">Azure services</div>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 mb-6">
        {services.map((t) => (
          <button key={t.id} data-testid={`az-tile-${t.id}`} onClick={() => onGo(t.id)} className="flex flex-col items-center gap-2 text-center p-3 rounded-md border border-border bg-card hover:border-[#0078d4] transition-colors">
            <NavGlyph item={t} size={32} />
            <div className="text-xs font-medium leading-tight">{t.label}</div>
          </button>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <div className="border border-border rounded-md bg-card">
          <div className="px-4 py-2.5 border-b border-border text-sm font-semibold flex items-center gap-2"><AzureIcon icon="recent" size={18} /> Recent</div>
          <div className="divide-y divide-border">
            {(recent || []).length === 0 && <div className="px-4 py-3 text-sm text-muted-foreground">Resources you visit will appear here.</div>}
            {(recent || []).map((id) => resById(id) && (
              <button key={id} onClick={() => onGo(id)} className="w-full px-4 py-2.5 flex items-center gap-2.5 hover:bg-muted text-left">
                <NavGlyph item={resById(id)} /> <span className="text-sm flex-1">{resById(id).label}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="border border-border rounded-md bg-card">
          <div className="px-4 py-2.5 border-b border-border text-sm font-semibold">Navigate</div>
          <div className="divide-y divide-border">
            {navItems.map((n) => (
              <button key={n.id} onClick={() => onGo(n.id)} className="w-full px-4 py-2.5 flex items-center gap-2.5 hover:bg-muted text-left">
                <AzureIcon icon={n.img} size={18} /> <span className="text-sm flex-1">{n.label}</span>
              </button>
            ))}
            <div className="px-4 py-2.5 flex items-center gap-2.5"><GraduationCap className="h-[18px] w-[18px] text-[#0078d4]" /> <span className="text-sm flex-1">Labs completed</span><span className="text-sm font-semibold">{completed}/{(state.labs || []).length}</span></div>
          </div>
        </div>
      </div>

      <p className="mt-8 text-xs text-muted-foreground border-t border-border pt-4" data-testid="az-disclaimer">
        HMG Azure Training Lab — an independent educational simulator, not affiliated with or endorsed by Microsoft. Microsoft product icons are used for training under Microsoft's icon terms. All resources, identifiers and costs are synthetic training data.
      </p>
    </div>
  );
}

function Dashboard({ state, onGo }) {
  const count = (t) => (state.resources || []).filter((r) => r.type === t).length;
  const tiles = [
    { label: "Virtual machines", img: "virtual-machine", value: count("Microsoft.Compute/virtualMachines"), view: "vms" },
    { label: "Virtual networks", img: "virtual-network", value: count("Microsoft.Network/virtualNetworks"), view: "vnets" },
    { label: "Users", img: "users", value: state.entra_users.length, view: "entra-users" },
    { label: "Groups", img: "groups", value: state.entra_groups.length, view: "entra-groups" },
    { label: "Resource groups", img: "resource-group", value: state.resource_groups.length, view: "resource-groups" },
    { label: "All resources", img: "all-resources", value: (state.resources || []).length, view: "all-resources" },
  ];
  return (
    <div data-testid="az-dashboard">
      <div className="flex items-center gap-2.5 mb-4"><AzureIcon icon="dashboard" size={28} /><h1 className="font-semibold text-[22px]">Dashboard</h1></div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {tiles.map((t) => (
          <button key={t.label} onClick={() => onGo(t.view)} className="border border-border rounded-md bg-card p-4 text-left hover:border-[#0078d4] transition-colors" style={{ boxShadow: "var(--az-elev)" }}>
            <div className="flex items-center gap-2 mb-2"><AzureIcon icon={t.img} size={22} /><span className="text-xs text-muted-foreground truncate">{t.label}</span></div>
            <div className="font-semibold text-3xl">{t.value}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

function Subscriptions({ state }) {
  return (
    <div className="border border-border rounded-md overflow-hidden bg-card az-grid" data-testid="az-subscriptions">
      <Table>
        <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Subscription ID</TableHead><TableHead>State</TableHead></TableRow></TableHeader>
        <TableBody>
          {state.subscriptions.map((s) => (
            <TableRow key={s.id}><TableCell className="font-medium text-[#0078d4]"><span className="inline-flex items-center gap-2"><AzureIcon icon="subscription" size={18} />{s.name}</span></TableCell><TableCell className="font-mono text-xs text-muted-foreground">{s.id}</TableCell><TableCell><span className="text-emerald-600 text-xs font-medium">{s.state}</span></TableCell></TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function ActivityLog({ state }) {
  const statusColor = (s) => s === "Succeeded" ? "text-emerald-600" : s === "Failed" ? "text-red-600" : "text-amber-600";
  return (
    <div className="border border-border rounded-md overflow-hidden bg-card az-grid" data-testid="az-activity-log">
      <Table>
        <TableHeader><TableRow><TableHead>Operation</TableHead><TableHead>Status</TableHead><TableHead>Initiated by</TableHead><TableHead>Resource</TableHead><TableHead>Time</TableHead></TableRow></TableHeader>
        <TableBody>
          {state.activity_log.map((e) => (
            <TableRow key={e.id}>
              <TableCell className="font-medium">{e.operation}</TableCell>
              <TableCell className={`text-xs font-medium ${statusColor(e.status)}`}>{e.status}</TableCell>
              <TableCell className="text-xs text-muted-foreground">{e.initiated_by}</TableCell>
              <TableCell className="text-xs">{e.resource_name}</TableCell>
              <TableCell className="text-xs text-muted-foreground">{new Date(e.timestamp).toLocaleString()}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
